import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import PortalHeader from '../../../components/Portal/PortalHeader';
import { useAuth } from '../../../contexts/AuthContext/AuthContext';
import { fetchAllEvents, updateEvent } from '../../../services/eventService';
import { fetchAllSongs } from '../../../services/SongRegistryService';
import { getRepertoireForSong } from '../../../services/RepertoireService';
import useTrackPlayer from '../../../components/Tracks/useTrackPlayer';
import useRecitalTracks from './useRecitalTracks';
import RecitalTrackLines, { readyCount } from './RecitalTrackLines';
import { fetchStudents } from '../../../services/studentService';
import {
	normalizeSetList,
	addSongToSetList,
	removeSetListRow,
	moveSetListRow,
	updateSetListRow,
	deriveRehearsalRoster,
	orphanedSongIds,
	splitEvenly,
	proposePerformers,
	hasSong,
} from '../../../services/recitalPlanModel';
import './RecitalPlanning.css';

/**
 * Recital planning — the set list, and which songs each rehearsal runs.
 *
 * STAFF-ONLY v1 (decision 3, Anthony 2026-08-06). This route lives under the
 * employee/admin guard and the existing `events` rules already fit it exactly:
 * staff read with viewSchedule, admin/owner write. Nothing here may grow a
 * customer branch — family-facing call sheets are a later card, and print is the
 * interim channel.
 *
 * THE PAGE OWNS NO DATA OF ITS OWN. A recital and its rehearsals are `events`
 * docs created on the Calendar; this page edits two fields on them —
 * `setList` on the recital, `songIds` on each rehearsal — and DERIVES everything
 * else. In particular a rehearsal's roster is computed on every render from the
 * set list (recitalPlanModel.deriveRehearsalRoster) and is never written, so a
 * cast change on the program cannot leave a rehearsal calling the wrong student.
 *
 * The legacy Recital Songs page continues to exist and is NOT a rival: it is
 * suggestion intake and song prep (keys, karaoke, lyrics). Its 35/92 free-typed
 * name strings are deliberately not imported — they carry no key to join on,
 * which is the whole reason the canonical registry was built.
 */

const dateOf = (event) => event?.specificDate || event?.startDate || '';

const fmtDate = (value) => {
	if (!value) return 'No date';
	// specificDate is a 'YYYY-MM-DD' string; parse as LOCAL, not UTC — `new
	// Date('2027-01-24')` is midnight UTC and prints as the 23rd in Minnesota.
	const [y, m, d] = String(value).split('-').map(Number);
	if (!y || !m || !d) return String(value);
	return new Date(y, m - 1, d).toLocaleDateString('en-US', {
		weekday: 'short',
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	});
};

const fmtTime = (hhmm) => {
	if (!hhmm) return '';
	const [h, m] = String(hhmm).split(':').map(Number);
	if (Number.isNaN(h)) return String(hhmm);
	const period = h >= 12 ? 'PM' : 'AM';
	const hour = h % 12 === 0 ? 12 : h % 12;
	return `${hour}:${String(m || 0).padStart(2, '0')} ${period}`;
};

const songLabel = (song) =>
	[song?.title, song?.artist].filter(Boolean).join(' — ') || 'Untitled song';

const RecitalPlanning = () => {
	const { userRole } = useAuth();
	// Writes are admin/owner in firestore.rules. A teacher with viewSchedule can
	// READ this page, so the controls are hidden rather than left to fail against
	// the rules — a disabled-looking button that throws is worse than no button.
	const canEdit = userRole === 'admin' || userRole === 'owner';

	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [events, setEvents] = useState([]);
	const [songs, setSongs] = useState([]);
	const [students, setStudents] = useState([]);
	const [recitalId, setRecitalId] = useState('');
	const [saving, setSaving] = useState('');
	const [pickerValue, setPickerValue] = useState('');
	const [titleDrafts, setTitleDrafts] = useState({});

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				setLoading(true);
				const [allEvents, allSongs, allStudents] = await Promise.all([
					fetchAllEvents(),
					fetchAllSongs(),
					fetchStudents(),
				]);
				if (cancelled) return;
				setEvents(allEvents);
				setSongs(allSongs);
				setStudents(allStudents);
				const recitals = allEvents
					.filter((e) => e.type === 'recital')
					.sort((a, b) => String(dateOf(a)).localeCompare(String(dateOf(b))));
				// Default to the NEXT recital rather than the first ever: planning
				// happens forwards, and last season's program is history.
				const today = new Date().toISOString().slice(0, 10);
				const next = recitals.find((r) => String(dateOf(r)) >= today) || recitals[0];
				if (next) setRecitalId(next.id);
			} catch (err) {
				console.error('Error loading recital planning data:', err);
				if (!cancelled) setError('Could not load recitals. Reload to try again.');
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	const recitals = useMemo(
		() =>
			events
				.filter((e) => e.type === 'recital')
				.sort((a, b) => String(dateOf(a)).localeCompare(String(dateOf(b)))),
		[events]
	);

	const recital = useMemo(
		() => recitals.find((r) => r.id === recitalId) || null,
		[recitals, recitalId]
	);

	const setList = useMemo(() => normalizeSetList(recital?.setList), [recital]);
	// Each performer's recital track (2026-09-27, tracks step 6): one player
	// for the page, so two tracks can never play over each other.
	const trackPlayer = useTrackPlayer();
	const recitalTracks = useRecitalTracks(setList);
	const songsById = useMemo(() => Object.fromEntries(songs.map((s) => [s.id, { canonicalSongId: s.id, title: s.title }])), [songs]);
	const tracksReady = useMemo(() => readyCount(setList, recitalTracks, songsById), [setList, recitalTracks, songsById]);

	const rehearsals = useMemo(
		() =>
			events
				.filter((e) => e.type === 'rehearsal' && e.recitalId === recitalId)
				.sort((a, b) =>
					`${dateOf(a)} ${a.startTime || ''}`.localeCompare(`${dateOf(b)} ${b.startTime || ''}`)
				),
		[events, recitalId]
	);

	const studentName = useCallback(
		(id) => {
			const student = students.find((s) => s.id === id);
			return student ? student.fullName || student.firstName || id : id;
		},
		[students]
	);

	/** Persist one event doc and mirror it locally — no refetch, no lost scroll. */
	const persist = useCallback(async (eventId, patch, label) => {
		setSaving(label || 'Saving…');
		try {
			await updateEvent(eventId, patch);
			setEvents((prev) => prev.map((e) => (e.id === eventId ? { ...e, ...patch } : e)));
			setSaving('Saved');
			setError('');
		} catch (err) {
			console.error('Error saving recital plan:', err);
			setSaving('');
			setError('That change did not save. Your screen may be out of date — reload.');
		}
	}, []);

	const saveSetList = useCallback(
		(next) => recital && persist(recital.id, { setList: next }, 'Saving set list…'),
		[recital, persist]
	);

	/**
	 * Add a song from the registry, seeding its cast from the canonical join.
	 *
	 * The join PROPOSES — every student with a repertoire row pointing at this
	 * registry doc — and the human disposes by editing the row afterwards. A
	 * failed lookup adds the song with NO performers rather than blocking: an
	 * empty cast is visibly incomplete, while a missing song is invisible.
	 */
	const addSong = useCallback(
		async (canonicalSongId) => {
			if (!recital || !canonicalSongId || hasSong(setList, canonicalSongId)) return;
			const song = songs.find((s) => s.id === canonicalSongId);
			setSaving('Finding performers…');
			let performers = [];
			try {
				performers = proposePerformers(await getRepertoireForSong(canonicalSongId));
			} catch (err) {
				console.error('Error proposing performers:', err);
			}
			await saveSetList(
				addSongToSetList(setList, {
					canonicalSongId,
					title: song?.title || '',
					performers,
				})
			);
			setPickerValue('');
		},
		[recital, setList, songs, saveSetList]
	);

	const availableSongs = useMemo(
		() => songs.filter((s) => !hasSong(setList, s.id)),
		[songs, setList]
	);

	const rosterFor = useCallback(
		(rehearsal) => deriveRehearsalRoster(setList, rehearsal.songIds),
		[setList]
	);

	const toggleSongOnRehearsal = useCallback(
		(rehearsal, canonicalSongId) => {
			const current = (rehearsal.songIds || []).filter(Boolean);
			const next = current.includes(canonicalSongId)
				? current.filter((id) => id !== canonicalSongId)
				: [...current, canonicalSongId];
			persist(rehearsal.id, { songIds: next }, 'Saving rehearsal…');
		},
		[persist]
	);

	const clearOrphans = useCallback(
		(rehearsal) => {
			const stale = orphanedSongIds(setList, rehearsal.songIds);
			const next = (rehearsal.songIds || []).filter((id) => !stale.includes(id));
			persist(rehearsal.id, { songIds: next }, 'Clearing…');
		},
		[setList, persist]
	);

	/** First draft only — it REPLACES every placement, so it asks first. */
	const runSplitEvenly = useCallback(async () => {
		if (!rehearsals.length || !setList.length) return;
		if (
			// eslint-disable-next-line no-alert
			!window.confirm(
				`Replace the songs placed on all ${rehearsals.length} rehearsal(s) with an even split of the ${setList.length}-song program?`
			)
		)
			return;
		const plan = splitEvenly(
			setList,
			rehearsals.map((r) => r.id)
		);
		for (const rehearsal of rehearsals) {
			// Sequential on purpose: a handful of docs, and a partial failure should
			// stop rather than leave half the rehearsals silently rewritten.
			// eslint-disable-next-line no-await-in-loop
			await persist(rehearsal.id, { songIds: plan[rehearsal.id] || [] }, 'Splitting…');
		}
	}, [rehearsals, setList, persist]);

	if (loading) {
		return (
			<Container className='py-4 recital-planning'>
				<PortalHeader title='Recital Planning' />
				<p className='rp-status'>
					<i className='fa-solid fa-spinner fa-spin'></i> Loading recitals…
				</p>
			</Container>
		);
	}

	return (
		<Container className='py-4 recital-planning'>
			<PortalHeader
				title='Recital Planning'
				subtitle='The set list, and which songs each rehearsal runs.'
			/>

			{error && <p className='rp-status rp-status--error'>{error}</p>}

			{recitals.length === 0 ? (
				<div className='rp-empty'>
					<i className='fa-solid fa-star'></i>
					<p>No recitals on the calendar yet.</p>
					<span>
						A recital is a calendar event — create one under Scheduling → Calendar, then
						plan its program here.
					</span>
				</div>
			) : (
				<>
					<div className='rp-toolbar'>
						<label className='rp-field'>
							<span className='rp-field__label'>Recital</span>
							<select
								className='rp-select'
								value={recitalId}
								onChange={(e) => setRecitalId(e.target.value)}
							>
								{recitals.map((r) => (
									<option key={r.id} value={r.id}>
										{`${r.title || 'Recital'} · ${fmtDate(dateOf(r))}`}
									</option>
								))}
							</select>
						</label>
						{saving && <span className='rp-saving'>{saving}</span>}
					</div>

					{recital && (
						<>
							<section className='rp-panel'>
								<div className='rp-panel__head'>
									<h2>
										<i className='fa-solid fa-list-ol'></i> Set list
									</h2>
									<span className='rp-count'>
										{setList.length} {setList.length === 1 ? 'song' : 'songs'}
										{tracksReady && tracksReady.total > 0
											? ` · recital tracks ${tracksReady.ready} of ${tracksReady.total}`
											: ''}
									</span>
								</div>

								{recital.venue && <p className='rp-venue'>{recital.venue}</p>}

								{setList.length === 0 && (
									<p className='rp-status'>
										Nothing in the program yet — add the first song below.
									</p>
								)}

								<ol className='rp-setlist'>
									{setList.map((entry, index) => (
										<li key={entry.canonicalSongId} className='rp-row'>
											<span className='rp-row__num'>{index + 1}</span>
											<div className='rp-row__body'>
												{canEdit ? (
													<input
														className='rp-row__title'
														aria-label={`Program title for song ${index + 1}`}
														value={
															titleDrafts[entry.canonicalSongId] ?? entry.title
														}
														onChange={(e) =>
															setTitleDrafts((prev) => ({
																...prev,
																[entry.canonicalSongId]: e.target.value,
															}))
														}
														// Committed on blur/Enter, not per keystroke: this
														// writes a whole event doc, and a title is typed a
														// character at a time.
														onBlur={() => {
															const draft = titleDrafts[entry.canonicalSongId];
															if (draft === undefined || draft === entry.title)
																return;
															saveSetList(
																updateSetListRow(setList, index, { title: draft })
															);
														}}
														onKeyDown={(e) => {
															if (e.key === 'Enter') e.currentTarget.blur();
														}}
													/>
												) : (
													<span className='rp-row__title rp-row__title--static'>
														{entry.title || 'Untitled'}
													</span>
												)}
												<div className='rp-performers'>
													{entry.performers.length === 0 ? (
														<span className='rp-performers__empty'>
															No performers yet
														</span>
													) : (
														entry.performers.map((studentId) => (
															<span key={studentId} className='rp-chip'>
																{studentName(studentId)}
																{canEdit && (
																	<button
																		type='button'
																		className='rp-chip__x'
																		aria-label={`Remove ${studentName(
																			studentId
																		)} from ${entry.title || 'this song'}`}
																		onClick={() =>
																			saveSetList(
																				updateSetListRow(setList, index, {
																					performers: entry.performers.filter(
																						(id) => id !== studentId
																					),
																				})
																			)
																		}
																	>
																		<i className='fa-solid fa-xmark'></i>
																	</button>
																)}
															</span>
														))
													)}
													{canEdit && (
														<select
															className='rp-addperformer'
															aria-label={`Add a performer to song ${index + 1}`}
															value=''
															onChange={(e) => {
																if (!e.target.value) return;
																saveSetList(
																	updateSetListRow(setList, index, {
																		performers: [
																			...entry.performers,
																			e.target.value,
																		],
																	})
																);
															}}
														>
															<option value=''>+ performer</option>
															{students
																.filter((s) => !entry.performers.includes(s.id))
																.map((s) => (
																	<option key={s.id} value={s.id}>
																		{s.fullName || s.firstName || s.id}
																	</option>
																))}
														</select>
													)}
												</div>
												<RecitalTrackLines
													entry={entry}
													song={songsById[entry.canonicalSongId]}
													data={recitalTracks}
													player={trackPlayer}
													studentName={studentName}
												/>
											</div>
											{canEdit && (
												<div className='rp-row__actions'>
													<button
														type='button'
														className='rp-iconbtn'
														title='Move up'
														aria-label={`Move song ${index + 1} up`}
														disabled={index === 0}
														onClick={() =>
															saveSetList(moveSetListRow(setList, index, -1))
														}
													>
														<i className='fa-solid fa-chevron-up'></i>
													</button>
													<button
														type='button'
														className='rp-iconbtn'
														title='Move down'
														aria-label={`Move song ${index + 1} down`}
														disabled={index === setList.length - 1}
														onClick={() =>
															saveSetList(moveSetListRow(setList, index, 1))
														}
													>
														<i className='fa-solid fa-chevron-down'></i>
													</button>
													<button
														type='button'
														className='rp-iconbtn rp-iconbtn--danger'
														title='Remove from the program'
														aria-label={`Remove song ${index + 1} from the program`}
														onClick={() =>
															saveSetList(removeSetListRow(setList, index))
														}
													>
														<i className='fa-solid fa-trash-can'></i>
													</button>
												</div>
											)}
										</li>
									))}
								</ol>

								{canEdit && (
									<div className='rp-add'>
										<select
											className='rp-select'
											aria-label='Add a song to the program'
											value={pickerValue}
											onChange={(e) => {
												setPickerValue(e.target.value);
												addSong(e.target.value);
											}}
										>
											<option value=''>Add a song…</option>
											{availableSongs.map((song) => (
												<option key={song.id} value={song.id}>
													{songLabel(song)}
												</option>
											))}
										</select>
										{songs.length === 0 && (
											<span className='rp-hint'>
												No songs in the registry yet — songs appear here as they are
												added in lessons.
											</span>
										)}
										{songs.length > 0 && availableSongs.length === 0 && (
											<span className='rp-hint'>
												Every registry song is already in this program.
											</span>
										)}
									</div>
								)}
							</section>

							<section className='rp-panel'>
								<div className='rp-panel__head'>
									<h2>
										<i className='fa-solid fa-people-group'></i> Rehearsals
									</h2>
									{canEdit && rehearsals.length > 0 && setList.length > 0 && (
										<button type='button' className='rp-btn' onClick={runSplitEvenly}>
											Split the program evenly
										</button>
									)}
								</div>

								{rehearsals.length === 0 ? (
									<p className='rp-status'>
										No rehearsals linked to this recital. Create them on the Calendar as
										rehearsal events and point them at this recital.
									</p>
								) : (
									rehearsals.map((rehearsal) => {
										const placed = (rehearsal.songIds || []).filter(Boolean);
										const roster = rosterFor(rehearsal);
										const stale = orphanedSongIds(setList, placed);
										return (
											<div key={rehearsal.id} className='rp-rehearsal'>
												<div className='rp-rehearsal__head'>
													<span className='rp-rehearsal__title'>
														{rehearsal.title || 'Rehearsal'}
													</span>
													<span className='rp-rehearsal__when'>
														{fmtDate(dateOf(rehearsal))}
														{rehearsal.startTime
															? ` · ${fmtTime(rehearsal.startTime)}`
															: ''}
														{rehearsal.endTime ? `–${fmtTime(rehearsal.endTime)}` : ''}
													</span>
												</div>

												<div className='rp-rehearsal__songs'>
													{setList.length === 0 ? (
														<span className='rp-hint'>
															Build the set list first, then place songs here.
														</span>
													) : (
														setList.map((entry) => {
															const on = placed.includes(entry.canonicalSongId);
															return (
																<label
																	key={entry.canonicalSongId}
																	className={`rp-place${on ? ' is-on' : ''}`}
																>
																	<input
																		type='checkbox'
																		checked={on}
																		disabled={!canEdit}
																		onChange={() =>
																			toggleSongOnRehearsal(
																				rehearsal,
																				entry.canonicalSongId
																			)
																		}
																	/>
																	<span>{entry.title || 'Untitled'}</span>
																</label>
															);
														})
													)}
												</div>

												{stale.length > 0 && (
													<p className='rp-status rp-status--warn'>
														{stale.length} placed{' '}
														{stale.length === 1 ? 'song is' : 'songs are'} no longer in
														the program.{' '}
														{canEdit && (
															<button
																type='button'
																className='rp-linkbtn'
																onClick={() => clearOrphans(rehearsal)}
															>
																Clear
															</button>
														)}
													</p>
												)}

												{/* DERIVED, never stored — recomputed from the set list on
												    every render, so a cast edit cannot strand a roster. */}
												<div className='rp-roster'>
													<span className='rp-roster__label'>
														Called ({roster.length})
													</span>
													{roster.length === 0 ? (
														<span className='rp-performers__empty'>
															Nobody yet — place a song above.
														</span>
													) : (
														roster.map((studentId) => (
															<span key={studentId} className='rp-chip'>
																{studentName(studentId)}
															</span>
														))
													)}
												</div>

												{(rehearsal.cast || []).length > 0 && (
													<p className='rp-hint'>
														{(rehearsal.cast || []).length} student
														{(rehearsal.cast || []).length === 1 ? '' : 's'} added
														directly on the Calendar; the called list above comes from
														the set list.
													</p>
												)}
											</div>
										);
									})
								)}
							</section>

							{(recital.callTimes || []).length > 0 && (
								<section className='rp-panel'>
									<div className='rp-panel__head'>
										<h2>
											<i className='fa-solid fa-clock'></i> Call times
										</h2>
									</div>
									<ul className='rp-calltimes'>
										{(recital.callTimes || []).map((ct, i) => (
											<li key={`${ct.label}-${i}`}>
												<span className='rp-calltimes__time'>{fmtTime(ct.time)}</span>
												<span>{ct.label}</span>
											</li>
										))}
									</ul>
									<p className='rp-hint'>
										Call times are edited with the recital event on the Calendar.
									</p>
								</section>
							)}

							{!canEdit && (
								<p className='rp-hint'>
									You can read this plan; editing a recital is admin-only.
								</p>
							)}
						</>
					)}
				</>
			)}
		</Container>
	);
};

export default RecitalPlanning;
